import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import Reveal from "./Reveal";
import { SITE, SERVICE_TYPES, FREQUENCIES, CONFIRMATION_MESSAGE } from "../config/site";

// Web3Forms access key. Safe to ship in frontend code — it only permits
// submitting to the inbox it was registered against.
const WEB3FORMS_KEY =
  process.env.REACT_APP_WEB3FORMS_KEY || "720efb4f-25cd-4c56-a334-0d689d67111b";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const initial = {
  name: "",
  phone: "",
  email: "",
  service_area: "",
  service_type: "",
  frequency: "",
  size: "",
  preferred_date: "",
  notes: "",
};

const inputCls =
  "mt-2 h-12 rounded-lg border-ink/15 bg-white px-4 text-base text-ink placeholder:text-slateblue/50 focus-visible:ring-seafoam";

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [botcheck, setBotcheck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_type || !form.frequency) {
      setError("Please choose a service type and frequency.");
      toast.error("Please choose a service type and frequency.");
      return;
    }
    setError("");
    setSubmitting(true);

    // Keys become the field labels in the notification email, so they are
    // written the way they should read in the inbox.
    const payload = {
      access_key: WEB3FORMS_KEY,
      subject: `New quote request — ${form.name}`,
      from_name: "Rock Coast Cleaning Website",
      replyto: form.email,
      botcheck,
      Name: form.name,
      Phone: form.phone,
      Email: form.email,
      "Service Area": form.service_area || "Not specified",
      "Type of Service": form.service_type,
      Frequency: form.frequency,
      "Approximate Size": form.size || "Not specified",
      "Preferred Date / Timeframe": form.preferred_date || "Not specified",
      "Additional Notes": form.notes || "None",
    };

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

if (result.success) {
  // Track successful quote requests as Meta Leads
  if (typeof window.fbq === "function") {
    window.fbq("track", "Lead");
  }

  setDone(true);
} else {
  throw new Error(result.message || "Submission rejected");
}
    } catch (err) {
      setError(
        "Something went wrong sending your request. Please email us directly at " + SITE.email
      );
      toast.error(
        "Something went wrong sending your request. Please email us directly at " + SITE.email
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="bg-mist py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[1fr_1.4fr] lg:gap-24 lg:px-12">
        <Reveal>
          <p className="eyebrow">Free quote</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Tell us about <span className="italic text-ocean">your space</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slateblue">
            Honest quotes, flat-rate options, no pressure — always a free, no-obligation
            quote that actually reflects your space. Prefer to reach out directly? Email{" "}
            <a href={`mailto:${SITE.email}`} data-testid="quote-email-link" className="font-semibold text-ocean underline underline-offset-4 hover:text-seafoam">
              {SITE.email}
            </a>
            {SITE.facebook && (
              <>
                {" "}or message us on{" "}
                <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="quote-facebook-link" className="font-semibold text-ocean underline underline-offset-4 hover:text-seafoam">
                  Facebook
                </a>
              </>
            )}
            .
          </p>
          <p className="mt-8 inline-block rounded-full border border-ocean/30 px-5 py-2 text-xs font-bold uppercase tracking-[0.25em] text-ocean" data-testid="quote-reply-promise">
            {SITE.replyPromise}
          </p>
        </Reveal>

        <Reveal i={1} className="rounded-3xl bg-white p-8 shadow-[0_40px_100px_-40px_rgba(14,53,39,0.35)] lg:p-12">
          {done ? (
            <div className="flex h-full min-h-[420px] flex-col items-start justify-center" data-testid="quote-success-message">
              <CheckCircle2 className="h-12 w-12 text-seafoam" strokeWidth={1.6} />
              <h3 className="mt-6 font-serif text-3xl tracking-tight text-ink">Request received</h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-slateblue">{CONFIRMATION_MESSAGE}</p>
              <Button
                data-testid="quote-another-button"
                variant="outline"
                className="mt-8 rounded-full border-ink/20 px-6"
                onClick={() => { setForm(initial); setDone(false); }}
              >
                Send another request
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="quote-form" className="grid gap-6 sm:grid-cols-2">
              {/* Honeypot — hidden from people, filled in by bots.
                  Must be a display:none checkbox: an offscreen text input
                  still gets populated by browser autofill, which trips the
                  check and causes Web3Forms to reject real submissions. */}
              <input
                type="checkbox"
                name="botcheck"
                checked={botcheck}
                onChange={(e) => setBotcheck(e.target.checked)}
                className="hidden"
                style={{ display: "none" }}
                tabIndex={-1}
                aria-hidden="true"
              />

              <div>
                <Label htmlFor="q-name" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Name *</Label>
                <Input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="q-phone" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Phone *</Label>
                <Input id="q-phone" data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(709) 000-0000" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="q-email" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Email *</Label>
                <Input id="q-email" data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="q-area" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Service Area</Label>
                <Input id="q-area" data-testid="quote-area-input" value={form.service_area} onChange={set("service_area")} placeholder="e.g. Mount Pearl" className={inputCls} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Type of Service *</Label>
                <Select value={form.service_type} onValueChange={set("service_type")}>
                  <SelectTrigger data-testid="quote-service-select" className={inputCls}>
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Frequency *</Label>
                <Select value={form.frequency} onValueChange={set("frequency")}>
                  <SelectTrigger data-testid="quote-frequency-select" className={inputCls}>
                    <SelectValue placeholder="How often?" />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f} value={f} data-testid={`quote-frequency-option-${f.toLowerCase()}`}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="q-size" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Approximate Size</Label>
                <Input id="q-size" data-testid="quote-size-input" value={form.size} onChange={set("size")} placeholder="e.g. 3 bed / 2 bath, or 1,800 sq ft" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="q-date" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Preferred Date / Timeframe</Label>
                <Input id="q-date" data-testid="quote-date-input" value={form.preferred_date} onChange={set("preferred_date")} placeholder="Optional" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="q-notes" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Anything else we should know?</Label>
                <Textarea
                  id="q-notes"
                  data-testid="quote-notes-input"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Pets, parking, access instructions — anything that helps (optional)"
                  className="mt-2 min-h-[110px] rounded-lg border-ink/15 bg-white px-4 py-3 text-base text-ink placeholder:text-slateblue/50 focus-visible:ring-seafoam"
                />
              </div>
              <div className="sm:col-span-2">
                {error && (
                  <p data-testid="quote-form-error" role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  data-testid="quote-form-submit-button"
                  disabled={submitting}
                  className="h-14 w-full rounded-full bg-ocean text-sm font-bold uppercase tracking-[0.25em] text-salt transition-[background-color,transform] duration-300 hover:bg-seafoam hover:scale-[1.01] disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Get My Free Quote"}
                </Button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
